import React, { useState, useEffect } from 'react';
import {
  Cloud,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Edit,
  Check,
  Key,
  X,
  Lock,
  HardDrive,
  FolderTree,
  FileText,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAssetSettingsStore } from '../../../store/assetSettingsStore';
import { cn } from '../../../lib/utils';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { Badge } from '../../../components/Badge';

export const AWS_S3_REGIONS = [
  { id: 'ap-south-1', name: 'Asia Pacific (Mumbai) [ap-south-1]' },
  { id: 'ap-south-2', name: 'Asia Pacific (Hyderabad) [ap-south-2]' },
  { id: 'us-east-1', name: 'US East (N. Virginia) [us-east-1]' },
  { id: 'us-east-2', name: 'US East (Ohio) [us-east-2]' },
  { id: 'us-west-1', name: 'US West (N. California) [us-west-1]' },
  { id: 'us-west-2', name: 'US West (Oregon) [us-west-2]' },
  { id: 'eu-west-1', name: 'Europe (Ireland) [eu-west-1]' },
  { id: 'eu-west-2', name: 'Europe (London) [eu-west-2]' },
  { id: 'eu-west-3', name: 'Europe (Paris) [eu-west-3]' },
  { id: 'eu-central-1', name: 'Europe (Frankfurt) [eu-central-1]' },
  { id: 'eu-north-1', name: 'Europe (Stockholm) [eu-north-1]' },
  { id: 'ap-southeast-1', name: 'Asia Pacific (Singapore) [ap-southeast-1]' },
  { id: 'ap-southeast-2', name: 'Asia Pacific (Sydney) [ap-southeast-2]' },
  { id: 'ap-northeast-1', name: 'Asia Pacific (Tokyo) [ap-northeast-1]' },
  { id: 'ca-central-1', name: 'Canada (Central) [ca-central-1]' },
  { id: 'sa-east-1', name: 'South America (São Paulo) [sa-east-1]' },
];

interface AssetCloudStorageTabProps {
  setHeaderActions?: (actions: React.ReactNode) => void;
}

export function AssetCloudStorageTab({ setHeaderActions }: AssetCloudStorageTabProps = {}) {
  const {
    storage,
    fetchBackendStorageConfig,
    saveBackendStorageConfig,
    testStorageConnection,
    isTestingStorage,
    storageTestResult,
  } = useAssetSettingsStore();

  const [isEditing, setIsEditing] = useState(false);
  const [replaceSecretKey, setReplaceSecretKey] = useState(false);
  const [newSecretKey, setNewSecretKey] = useState('');

  // Form State
  const [formState, setFormState] = useState({
    s3_bucket: '',
    s3_region: 'ap-south-1',
    s3_prefix: 'Asset_Management_TB/',
    access_key_id: 'AKIA2HXTNWNM5DPTUSG3',
    max_file_size_mb: 50,
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchBackendStorageConfig();
  }, [fetchBackendStorageConfig]);

  useEffect(() => {
    if (storage) {
      setFormState({
        s3_bucket: storage.s3_bucket || '',
        s3_region: storage.s3_region || 'ap-south-1',
        s3_prefix: storage.s3_prefix || 'Asset_Management_TB/',
        access_key_id: storage.access_key_id || 'AKIA2HXTNWNM5DPTUSG3',
        max_file_size_mb: storage.max_file_size_mb || 50,
      });
    }
  }, [storage]);

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!formState.s3_bucket.trim()) {
      errs.s3_bucket = 'S3 Bucket Name is required.';
    } else if (!/^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/.test(formState.s3_bucket.trim())) {
      errs.s3_bucket = 'Bucket name must be 3-63 lowercase alphanumeric characters, dots, or hyphens.';
    }

    if (!formState.s3_region) {
      errs.s3_region = 'AWS Region is required.';
    }

    if (isEditing && replaceSecretKey && !newSecretKey.trim()) {
      errs.secret_key = 'Please enter a valid AWS Secret Access Key.';
    }

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) {
      toast.error('Please fix validation errors before saving.');
      return;
    }

    const payload: any = {
      s3_bucket: formState.s3_bucket.trim(),
      s3_region: formState.s3_region,
      s3_prefix: formState.s3_prefix.trim() || 'Asset_Management_TB/',
      access_key_id: formState.access_key_id.trim(),
      max_file_size_mb: formState.max_file_size_mb,
    };

    if (replaceSecretKey && newSecretKey.trim()) {
      payload.secret_access_key = newSecretKey.trim();
    }

    try {
      await saveBackendStorageConfig(payload);
      setIsEditing(false);
      setReplaceSecretKey(false);
      setNewSecretKey('');
      fetchBackendStorageConfig();
    } catch {
      // error handled in store
    }
  };

  useEffect(() => {
    if (!setHeaderActions) return;
    setHeaderActions(
      <div className="flex items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={testStorageConnection}
          isLoading={isTestingStorage}
          disabled={!storage?.s3_bucket}
        >
          <RefreshCw className="size-3.5 mr-1.5" /> Test S3 Connection
        </Button>

        {!isEditing ? (
          <Button
            type="button"
            size="sm"
            onClick={() => setIsEditing(true)}
          >
            <Edit className="size-3.5 mr-1.5" /> Edit Configuration
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => {
                setIsEditing(false);
                setReplaceSecretKey(false);
                setNewSecretKey('');
                setErrors({});
              }}
            >
              <X className="size-3.5 mr-1.5" /> Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
            >
              <Check className="size-3.5 mr-1.5" /> Save Changes
            </Button>
          </div>
        )}
      </div>
    );
    return () => setHeaderActions(null);
  }, [setHeaderActions, testStorageConnection, isTestingStorage, storage?.s3_bucket, isEditing, handleSave, replaceSecretKey, newSecretKey]);

  const isConfigured = Boolean(storage?.is_configured || storage?.s3_bucket);

  return (
    <div className="space-y-6">
      {/* Top Banner & Header (hidden if setHeaderActions is provided) */}
      {!setHeaderActions && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-border">
          <div>
            <h2 className="font-display text-section-title text-foreground flex items-center gap-2">
              <Cloud className="size-5 text-primary" /> ITAM Cloud S3 Storage Vault
            </h2>
            <p className="mt-0.5 text-xs sm:text-sm text-muted-foreground">
              Configure enterprise AWS S3 storage for hardware asset condition photos, device manuals, purchase tax invoices, and warranty documents.
            </p>
          </div>

          <div className="flex items-center gap-2.5 shrink-0">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={testStorageConnection}
              isLoading={isTestingStorage}
              disabled={!storage?.s3_bucket}
            >
              <RefreshCw className="size-3.5 mr-1.5" /> Test S3 Connection
            </Button>

            {!isEditing ? (
              <Button
                type="button"
                size="sm"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="size-3.5 mr-1.5" /> Edit Configuration
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setIsEditing(false);
                    setReplaceSecretKey(false);
                    setNewSecretKey('');
                    setErrors({});
                  }}
                >
                  <X className="size-3.5 mr-1.5" /> Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSave}
                >
                  <Check className="size-3.5 mr-1.5" /> Save Changes
                </Button>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Connection Status Card */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-xs">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'size-10 rounded-lg flex items-center justify-center shrink-0 border',
                isConfigured
                  ? 'bg-success/10 text-success-strong border-success/20'
                  : 'bg-warning/10 text-warning-strong border-warning/20'
              )}
            >
              {isConfigured ? (
                <CheckCircle2 className="size-5" />
              ) : (
                <AlertCircle className="size-5" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-semibold text-sm text-foreground">
                  {isConfigured ? 'AWS S3 Cloud Vault Active' : 'S3 Storage Not Fully Configured'}
                </span>
                <Badge variant={isConfigured ? 'success' : 'warning'}>
                  {isConfigured ? 'Configured' : 'Missing Bucket'}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isConfigured
                  ? `Hardware photos & invoices stream to bucket "${storage?.s3_bucket}" under prefix "${storage?.s3_prefix || 'Asset_Management_TB/'}"`
                  : 'Configure and save your S3 Bucket Name and AWS credentials below to enable cloud uploads.'}
              </p>
            </div>
          </div>

          {storageTestResult && (
            <div
              className={cn(
                'p-2.5 rounded-lg border text-xs sm:max-w-md',
                storageTestResult.success
                  ? 'bg-success/10 border-success/30 text-success-strong'
                  : 'bg-destructive/10 border-destructive/30 text-destructive-strong'
              )}
            >
              <span className="font-semibold block mb-0.5">
                {storageTestResult.success ? 'Probe Succeeded' : 'Probe Failed'}
              </span>
              <p className="text-[11px] leading-snug">{storageTestResult.message}</p>
            </div>
          )}
        </div>
      </div>

      {/* S3 Directory Architecture Overview */}
      <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-2.5">
        <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
          <FolderTree className="size-4 text-primary" /> Storage Directory Architecture
        </h3>
        <p className="text-xs text-muted-foreground leading-relaxed">
          Files are automatically partitioned into an isolated, per-device hierarchy inside your S3 bucket:
        </p>
        <div className="font-mono text-xs bg-background/80 rounded-lg p-3 border border-border space-y-1 text-foreground">
          <div className="text-primary font-semibold">s3://{formState.s3_bucket || 'YOUR_BUCKET_NAME'}/</div>
          <div className="pl-4 text-muted-foreground">└── Asset_Management_TB/</div>
          <div className="pl-8 text-foreground">├── TBA00001/</div>
          <div className="pl-12 text-muted-foreground">├── TBA00001IMG001.webp <span className="text-[11px] font-sans text-muted-foreground/80">(Hardware Photo 1)</span></div>
          <div className="pl-12 text-muted-foreground">├── TBA00001IMG002.jpg  <span className="text-[11px] font-sans text-muted-foreground/80">(Hardware Photo 2)</span></div>
          <div className="pl-12 text-muted-foreground">├── TBA00001DOC001.pdf  <span className="text-[11px] font-sans text-muted-foreground/80">(Tax Invoice)</span></div>
          <div className="pl-12 text-muted-foreground">└── TBA00001DOC002.pdf  <span className="text-[11px] font-sans text-muted-foreground/80">(Warranty Certificate)</span></div>
          <div className="pl-8 text-foreground">├── TBA00002/</div>
          <div className="pl-12 text-muted-foreground">└── ...</div>
        </div>
      </div>

      {/* S3 Configuration Form Card */}
      <Card className="p-5 space-y-5">
        <h3 className="font-display text-card-title text-foreground flex items-center gap-2">
          <HardDrive className="size-4 text-primary" /> AWS S3 Connection Parameters
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* 1. S3 Bucket Name */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center justify-between">
              <span>S3 Bucket Name <span className="text-destructive">*</span></span>
              {errors.s3_bucket && <span className="text-destructive text-[11px] font-normal">{errors.s3_bucket}</span>}
            </label>
            <input
              type="text"
              disabled={!isEditing}
              value={formState.s3_bucket}
              onChange={(e) => setFormState({ ...formState, s3_bucket: e.target.value.toLowerCase() })}
              placeholder="e.g. teamone-asset-vault"
              className={cn(
                'w-full h-9 px-3 rounded-lg border bg-background text-xs font-mono text-foreground outline-none transition-colors focus:ring-1 focus:ring-primary',
                errors.s3_bucket ? 'border-destructive focus:border-destructive' : 'border-input focus:border-primary',
                !isEditing && 'opacity-70 cursor-not-allowed bg-secondary/30'
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              Target AWS S3 bucket name. Must adhere to AWS bucket naming conventions.
            </p>
          </div>

          {/* 2. AWS Region */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground flex items-center justify-between">
              <span>AWS Region <span className="text-destructive">*</span></span>
              {errors.s3_region && <span className="text-destructive text-[11px] font-normal">{errors.s3_region}</span>}
            </label>
            <select
              disabled={!isEditing}
              value={formState.s3_region}
              onChange={(e) => setFormState({ ...formState, s3_region: e.target.value })}
              className={cn(
                'w-full h-9 px-3 rounded-lg border bg-background text-xs text-foreground outline-none transition-colors focus:ring-1 focus:ring-primary',
                !isEditing && 'opacity-70 cursor-not-allowed bg-secondary/30'
              )}
            >
              {AWS_S3_REGIONS.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.name}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-muted-foreground">
              The AWS geographical region where your S3 bucket resides.
            </p>
          </div>

          {/* 3. Key Prefix */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              <span>Bucket Root Prefix</span>
            </label>
            <input
              type="text"
              disabled={!isEditing}
              value={formState.s3_prefix}
              onChange={(e) => setFormState({ ...formState, s3_prefix: e.target.value })}
              placeholder="Asset_Management_TB/"
              className={cn(
                'w-full h-9 px-3 rounded-lg border bg-background text-xs font-mono text-foreground outline-none transition-colors focus:ring-1 focus:ring-primary',
                !isEditing && 'opacity-70 cursor-not-allowed bg-secondary/30'
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              Root folder prepended to all asset files (defaults to <code className="font-mono">Asset_Management_TB/</code>).
            </p>
          </div>

          {/* 4. Max File Size (MB) */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold text-foreground">
              <span>Max Upload Size (MB)</span>
            </label>
            <input
              type="number"
              min={1}
              max={500}
              disabled={!isEditing}
              value={formState.max_file_size_mb}
              onChange={(e) => setFormState({ ...formState, max_file_size_mb: Number(e.target.value) })}
              className={cn(
                'w-full h-9 px-3 rounded-lg border bg-background text-xs text-foreground outline-none transition-colors focus:ring-1 focus:ring-primary',
                !isEditing && 'opacity-70 cursor-not-allowed bg-secondary/30'
              )}
            />
            <p className="text-[11px] text-muted-foreground">
              Maximum allowable file payload for high-resolution photos and multi-page invoices.
            </p>
          </div>
        </div>

        {/* AWS Credentials Section */}
        <div className="pt-4 border-t border-border space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                <Key className="size-3.5 text-primary" /> IAM API Access Credentials
              </h4>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Encrypted at rest using AES-256-GCM in PostgreSQL.
              </p>
            </div>
            {storage?.access_key_preview && (
              <Badge variant="outline" className="font-mono text-[11px]">
                Key: {storage.access_key_preview}
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Access Key ID */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground">
                AWS Access Key ID
              </label>
              <input
                type="text"
                disabled={!isEditing}
                value={formState.access_key_id}
                onChange={(e) => setFormState({ ...formState, access_key_id: e.target.value.trim() })}
                placeholder="AKIA..."
                className={cn(
                  'w-full h-9 px-3 rounded-lg border bg-background text-xs font-mono text-foreground outline-none transition-colors focus:ring-1 focus:ring-primary',
                  !isEditing && 'opacity-70 cursor-not-allowed bg-secondary/30'
                )}
              />
            </div>

            {/* Secret Access Key */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-foreground flex items-center justify-between">
                <span>AWS Secret Access Key</span>
                {errors.secret_key && <span className="text-destructive text-[11px] font-normal">{errors.secret_key}</span>}
              </label>

              {!isEditing ? (
                <div className="h-9 px-3 rounded-lg border border-input bg-secondary/30 flex items-center justify-between text-xs font-mono text-muted-foreground">
                  <span className="flex items-center gap-1.5">
                    <Lock className="size-3.5" />
                    {storage?.access_key_preview ? '••••••••••••••••••••••••••••••••' : 'Not configured'}
                  </span>
                  <span className="text-[10px] text-muted-foreground uppercase">Encrypted</span>
                </div>
              ) : !replaceSecretKey ? (
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-9 px-3 rounded-lg border border-input bg-secondary/30 flex items-center justify-between text-xs font-mono text-muted-foreground">
                    <span className="flex items-center gap-1.5">
                      <Lock className="size-3.5" /> ••••••••••••••••••••••••
                    </span>
                    <span className="text-[10px] text-success-strong font-semibold">Configured</span>
                  </div>
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => setReplaceSecretKey(true)}
                  >
                    Replace
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <input
                    type="password"
                    value={newSecretKey}
                    onChange={(e) => setNewSecretKey(e.target.value.trim())}
                    placeholder="Enter new 40-character secret key"
                    className="flex-1 h-9 px-3 rounded-lg border border-input bg-background text-xs font-mono text-foreground outline-none focus:ring-1 focus:ring-primary"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setReplaceSecretKey(false);
                      setNewSecretKey('');
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Security & Access Notice */}
        <div className="rounded-lg bg-info/10 border border-info/20 p-3 flex items-start gap-2.5 text-xs text-foreground">
          <FileText className="size-4 text-info-strong shrink-0 mt-0.5" />
          <div className="space-y-1">
            <span className="font-semibold text-info-strong block">
              S3 Direct Streaming & Automated Mapping
            </span>
            <p className="text-muted-foreground leading-relaxed">
              When hardware condition photos or tax invoices are uploaded in the Asset Registry, files are automatically stored under <code className="font-mono text-foreground font-semibold">Asset_Management_TB/&#123;assetTag&#125;/&#123;assetTag&#125;IMG001</code> and mapped to database URLs. Deleting a record in the app automatically removes both the S3 object and the database entry.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
